import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import "@/App.css";
import "@/index.css";
import "@/styles/legacy.css";
import "@/styles/site.css";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import { Toaster } from "sonner";
import { api } from "@/lib/api";
import Landing from "@/pages/Landing";
import CertLanding from "@/pages/CertLanding";
import Login from "@/pages/Login";
import FreeVerify from "@/pages/FreeVerify";
import FreeExam from "@/pages/FreeExam";
import Dashboard from "@/pages/Dashboard";
import ExamRunner from "@/pages/ExamRunner";
import Report from "@/pages/Report";
import PaymentStatus from "@/pages/PaymentStatus";
import Guarantee from "@/pages/Guarantee";
import Pricing from "@/pages/Pricing";
import AdminApp from "@/pages/admin/AdminApp";
import { Privacy, Terms, RefundPolicy, Contact } from "@/pages/Legal";
import { SiteHeader, SiteProvider } from "@/components/site/Chrome";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/me");
      if (data.authenticated === false) { setUser(false); return false; }
      setUser(data);
      return data;
    } catch {
      setUser(false);
      return false;
    }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);
  const value = useMemo(() => ({ user, setUser, refresh }), [user, refresh]);
  return <AuthCtx.Provider value={value}><SiteProvider>{children}</SiteProvider></AuthCtx.Provider>;
}

export const Header = SiteHeader;

function CertRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/${slug || ""}`} replace />;
}

function ScrollToHash() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const t = setTimeout(() => document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" }), 80);
      return () => clearTimeout(t);
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToHash />
        <Toaster position="top-center" toastOptions={{ className: "er-toast", duration: 3500 }} />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/cert/:slug" element={<CertRedirect />} />
          <Route path="/login" element={<Login />} />
          <Route path="/free" element={<FreeExam />} />
          <Route path="/free/verify" element={<FreeVerify />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/exam/:id" element={<ExamRunner />} />
          <Route path="/report/:id" element={<Report />} />
          <Route path="/payment/success" element={<PaymentStatus />} />
          <Route path="/payment/cancel" element={<PaymentStatus cancel />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/guarantee" element={<Guarantee />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/refund-policy" element={<RefundPolicy />} />
          <Route path="/cookies" element={<Navigate to="/privacy" replace />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/admin/*" element={<AdminApp />} />
          <Route path="/:slug" element={<CertLanding />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
